import { useQuery } from '@apollo/client/react'
import {
  ArrowRight,
  BriefcaseBusiness,
  CalendarRange,
  CircleDollarSign,
  Clock3,
  ShieldCheck,
  Star,
  Users,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import AdminNav from '../../components/admin/AdminNav'
import StatsCard from '../../components/admin/StatsCard'
import Button from '../../components/ui/Button'
import { useLang } from '../../contexts/LangContext'
import { ADMIN_STATS_QUERY, type AdminStatsData } from '../../graphql/admin'
import { normalizeLocale } from '../../lib/i18n'
import { formatAdminCurrency } from '../../components/admin/formatters'

export default function AdminDashboardPage() {
  const { locale, t } = useLang()
  const activeLocale = normalizeLocale(locale)
  const { data, loading, error, refetch } = useQuery<AdminStatsData>(ADMIN_STATS_QUERY)
  const stats = data?.adminStats

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <section className="rounded-[2rem] border border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_45%,#eff6ff_100%)] p-6 shadow-sm md:p-8">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary-600">
          {t('Διαχείριση', 'Admin')}
        </p>
        <h1 className="mt-3 text-4xl font-black tracking-tight text-slate-950 sm:text-5xl">
          {t('Κεντρικός πίνακας πλατφόρμας', 'Platform control center')}
        </h1>
        <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-600">
          {t(
            'Παρακολούθησε τα βασικά μεγέθη της αγοράς, άνοιξε τις εκκρεμείς επαληθεύσεις και μπες γρήγορα στις κρατήσεις που χρειάζονται παρέμβαση.',
            'Track core marketplace metrics, open pending verifications, and jump into bookings that need intervention.',
          )}
        </p>
      </section>

      <AdminNav />

      {error ? (
        <div className="mt-6 rounded-[1.5rem] border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
          <p>{t('Δεν φορτώθηκαν τα admin stats αυτή τη στιγμή.', 'The admin stats could not be loaded right now.')}</p>
          <div className="mt-4">
            <Button type="button" variant="secondary" onClick={() => void refetch()}>
              {t('Δοκίμασε ξανά', 'Try again')}
            </Button>
          </div>
        </div>
      ) : null}

      {loading && !stats ? (
        <p className="mt-8 text-sm text-slate-500">{t('Φορτώνουμε τα στοιχεία διαχείρισης...', 'Loading admin metrics...')}</p>
      ) : null}

      {stats ? (
        <>
          <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <StatsCard
              icon={<Users className="h-5 w-5" />}
              label={t('Σύνολο χρηστών', 'Total users')}
              value={String(stats.totalUsers)}
              helper={t('Όλοι οι λογαριασμοί πελατών, παρόχων και admins.', 'All customer, provider, and admin accounts.')}
            />
            <StatsCard
              icon={<BriefcaseBusiness className="h-5 w-5" />}
              label={t('Σύνολο παρόχων', 'Total providers')}
              value={String(stats.totalProviders)}
              helper={t('Προφίλ επαγγελματιών που έχουν δημιουργηθεί.', 'Provider profiles created on the platform.')}
              tone="emerald"
            />
            <StatsCard
              icon={<ShieldCheck className="h-5 w-5" />}
              label={t('Εκκρεμείς επαληθεύσεις', 'Pending verifications')}
              value={String(stats.pendingVerifications)}
              helper={t('Πάροχοι που περιμένουν έγκριση εγγράφων.', 'Providers waiting for document approval.')}
              tone="amber"
            />
            <StatsCard
              icon={<CalendarRange className="h-5 w-5" />}
              label={t('Κρατήσεις εβδομάδας', 'Bookings this week')}
              value={String(stats.bookingsThisWeek)}
              helper={t('Νέες κρατήσεις που δημιουργήθηκαν μέσα στην τρέχουσα εβδομάδα.', 'New bookings created during the current week.')}
            />
            <StatsCard
              icon={<Clock3 className="h-5 w-5" />}
              label={t('Κρατήσεις μήνα', 'Bookings this month')}
              value={String(stats.bookingsThisMonth)}
              helper={t('Συνολικός όγκος δημιουργίας κρατήσεων μέσα στον μήνα.', 'Monthly booking creation volume.')}
              tone="slate"
            />
            <StatsCard
              icon={<CircleDollarSign className="h-5 w-5" />}
              label={t('Έσοδα μήνα', 'Revenue this month')}
              value={formatAdminCurrency(stats.revenueThisMonthCents, activeLocale)}
              helper={t('Συγκεντρωμένα platform fees για τον τρέχοντα μήνα.', 'Platform fees collected during the current month.')}
              tone="emerald"
            />
            <StatsCard
              icon={<ShieldCheck className="h-5 w-5" />}
              label={t('Ενεργοί πάροχοι', 'Active providers')}
              value={String(stats.activeProviders)}
              helper={t('Επαληθευμένοι πάροχοι που δέχονται νέες δουλειές.', 'Verified providers currently accepting work.')}
              tone="blue"
            />
            <StatsCard
              icon={<Star className="h-5 w-5" />}
              label={t('Μέση αξιολόγηση πλατφόρμας', 'Average platform rating')}
              value={stats.averagePlatformRating.toFixed(2)}
              helper={t('Μέσο rating από τις δημοσιευμένες κριτικές.', 'Average rating across published reviews.')}
              tone="amber"
            />
            <StatsCard
              icon={<CalendarRange className="h-5 w-5" />}
              label={t('Σύνολο κρατήσεων', 'Total bookings')}
              value={String(stats.totalBookings)}
              helper={t('Όλες οι κρατήσεις που έχουν δημιουργηθεί μέχρι σήμερα.', 'All bookings created to date.')}
              tone="slate"
            />
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-2">
            <Link
              to="/admin/providers?status=pending"
              className="group rounded-[1.5rem] border border-amber-200 bg-amber-50 p-6 shadow-sm transition hover:border-amber-300 hover:bg-amber-100/70"
            >
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-amber-700">
                {t('Γρήγορη ενέργεια', 'Quick action')}
              </p>
              <h2 className="mt-3 text-2xl font-black tracking-tight text-slate-950">
                {t('Εκκρεμείς επαληθεύσεις', 'Pending verifications')} ({stats.pendingVerifications})
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-7 text-slate-600">
                {t(
                  'Άνοιξε απευθείας τη φιλτραρισμένη λίστα παρόχων που χρειάζονται approve ή reject.',
                  'Jump straight to the filtered provider list that needs approve or reject decisions.',
                )}
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-amber-800">
                {t('Μετάβαση στη λίστα παρόχων', 'Go to provider queue')}
                <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
              </span>
            </Link>

            <Link
              to="/admin/bookings"
              className="group rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm transition hover:border-primary-200 hover:bg-primary-50/40"
            >
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary-600">
                {t('Εποπτεία κρατήσεων', 'Booking oversight')}
              </p>
              <h2 className="mt-3 text-2xl font-black tracking-tight text-slate-950">
                {t('Άνοιξε τον πίνακα κρατήσεων', 'Open the bookings board')}
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-7 text-slate-600">
                {t(
                  'Χρησιμοποίησε φίλτρα status, ημερομηνιών και αναζήτησης για να βρεις γρήγορα τις κρατήσεις που χρειάζονται admin ακύρωση ή έλεγχο.',
                  'Use status, date, and search filters to quickly find bookings that need admin cancellation or review.',
                )}
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary-700">
                {t('Μετάβαση στις κρατήσεις', 'Open bookings')}
                <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
              </span>
            </Link>
          </div>
        </>
      ) : null}
    </div>
  )
}
